import { useState } from "react";
import { WarnIcon } from "@/components/icons";
import type { ModalConfig } from "@/components/Modal";
import type { Availability } from "@/services/rto";
import { dateLabel, dayMonth, shortDow, tueSatBetween } from "@/utils/format";

/* Change-path swap scheduling. Stations, addresses, window and per-day
   remaining slots all come from the availability endpoint; a day inside the
   window that the station's list doesn't carry is fully booked ("lotado"). */

export function ScheduleScreen({
  availability,
  fullDayWarn,
  busy,
  openModal,
  onConfirm,
}: {
  availability: Availability;
  fullDayWarn: boolean;
  busy: boolean;
  openModal: (config: ModalConfig) => void;
  onConfirm: (stationId: number, date: string) => void;
}) {
  const [stationId, setStationId] = useState<number | null>(null);
  const [date, setDate] = useState<string | null>(null);

  const station = availability.stations.find((s) => s.station_id === stationId) ?? null;
  const windowDays =
    availability.window_start && availability.window_end
      ? tueSatBetween(availability.window_start, availability.window_end)
      : (station?.days.map((d) => d.date) ?? []);
  const openDates = new Set(station?.days.map((d) => d.date) ?? []);

  return (
    <section className="screen-enter" data-testid="screen-schedule">
      <h2>Agende a troca da sua moto</h2>
      <p className="lpc-sub">
        Contrato assinado — cópia no seu email. Agora escolha onde e quando você troca sua moto
        pela Comfort 0km.
      </p>

      {availability.stations.map((s) => (
        <button
          key={s.station_id}
          className={"lpc-select" + (s.station_id === stationId ? " is-selected" : "")}
          data-testid={`station-${s.station_id}`}
          onClick={() => {
            setStationId(s.station_id);
            setDate(null);
          }}
        >
          <span className="lpc-select__title">{s.name}</span>
          {s.address ? <span className="lpc-select__desc">{s.address}</span> : null}
        </button>
      ))}

      {station ? (
        <div data-testid="dates-wrap">
          <p style={{ fontWeight: 700, fontSize: 15, marginTop: 24 }}>
            Escolha o dia{" "}
            <span style={{ color: "var(--g500)", fontWeight: 500 }}>
              (terça a sábado
              {availability.window_start && availability.window_end
                ? `, ${dayMonth(availability.window_start)} a ${dayMonth(availability.window_end)}`
                : ""}
              )
            </span>
          </p>
          <div className="lpc-dates">
            {windowDays.map((iso) => {
              const open = openDates.has(iso);
              return (
                <button
                  key={iso}
                  className={"lpc-date" + (date === iso ? " is-selected" : "")}
                  data-testid={`date-${iso}`}
                  disabled={!open}
                  onClick={() => open && setDate(iso)}
                >
                  <div className="lpc-date__dow">{shortDow(iso)}</div>
                  <div className="lpc-date__num">{dayMonth(iso)}</div>
                  {!open ? <div className="lpc-date__tag">lotado</div> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {fullDayWarn ? (
        <div className="lpc-warn" data-testid="full-day-warn">
          <WarnIcon />
          <span>
            Esse dia acabou de lotar. Atualizamos a agenda — escolhe outro dia, por favor.
          </span>
        </div>
      ) : null}

      <p className="lpc-sub" style={{ margin: "18px 0" }}>
        Sem hora marcada: chegue no dia agendado, procure o atendimento, entregue sua moto atual e
        saia com a nova.
      </p>
      <div className="lpc-warn">
        <WarnIcon />
        <span>
          O atendimento é só no dia agendado. Não tem encaixe em outra data — se não puder ir,
          escolha outro dia agora.
        </span>
      </div>

      <button
        className="btn btn--primary"
        data-testid="schedule-confirm"
        disabled={!station || !date || busy}
        onClick={() => {
          if (!station || !date) return;
          openModal({
            title: "Confirmar agendamento?",
            body: `${dateLabel(date)} · ${station.name}${station.address ? ` — ${station.address}` : ""}.`,
            danger: "Você só será atendido nesse dia. Sem encaixe em outra data.",
            confirmLabel: "Confirmar",
            cancelLabel: "Trocar data",
            onConfirm: () => onConfirm(station.station_id, date),
          });
        }}
      >
        {busy ? "Agendando…" : "Confirmar agendamento"}
      </button>
    </section>
  );
}
